import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatApiError } from '@/lib/formatApiError';
import { formatCount, formatCtr, formatWatch, rangeQuery } from '@/lib/channel';

const FALLBACK_TABS = [
  { id: 'search_keywords', label: 'Search keywords' },
  { id: 'external_pages', label: 'External pages' },
  { id: 'traffic_sources', label: 'Traffic sources' },
  { id: 'suggested_videos', label: 'Suggested videos' },
  { id: 'countries', label: 'Countries' },
  { id: 'devices', label: 'Devices' },
  { id: 'audience', label: 'Audience' },
];

export function ChannelTrendsNav({ preset, start, end, ready, connected, scopesOk }) {
  const [tab, setTab] = useState(FALLBACK_TABS[0].id);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const enabled = Boolean(ready && connected && scopesOk);
  const canQuery = enabled && (preset !== 'custom' || (start && end));

  const load = useCallback(async () => {
    if (!canQuery) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data: payload } = await api.get('/channel/analytics-trends', {
        params: rangeQuery(preset, start, end),
      });
      setData(payload);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not load YouTube trends'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [canQuery, preset, start, end]);

  useEffect(() => {
    load();
  }, [load]);

  const tabs = data?.tabs?.length ? data.tabs : FALLBACK_TABS;
  const section = data?.sections?.[tab];
  const rows = Array.isArray(section?.rows) ? section.rows : [];

  let body = null;
  if (!ready) {
    body = null;
  } else if (!connected) {
    body = (
      <p className="text-sm text-[#8b95a7]">
        Connect YouTube to see search keywords, referring pages, and where views come from.
      </p>
    );
  } else if (!scopesOk) {
    body = <p className="text-sm text-[#8b95a7]">Reconnect YouTube to read these reports.</p>;
  } else if (loading && !data) {
    body = <p className="text-sm text-[#7d8799]">Loading trends…</p>;
  } else if (section?.error) {
    body = <p className="text-sm text-[#8b95a7]">This report is not available for this range.</p>;
  } else if (!rows.length) {
    body = <p className="text-sm text-[#6d7788]">No rows in this range.</p>;
  } else {
    body = (
      <ul className="space-y-1 max-h-80 overflow-y-auto">
        {rows.map((row, index) => (
          <li key={`${row.code}-${index}`} className="flex items-center gap-3 rounded-2xl px-2 py-2">
            <span className="w-6 text-sm font-medium text-[#5c6778]">{index + 1}</span>
            <span className="min-w-0 flex-1 text-sm text-white truncate">{row.label || row.code}</span>
            <span className="shrink-0 text-[11px] text-[#8b95a7]">
              {formatCount(row.views)}
              {row.watch_minutes != null ? ` · ${formatWatch(row.watch_minutes)}` : ''}
              {` · ${formatCtr(row.share)}`}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div data-testid="channel-trends-nav" className="space-y-3">
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {tabs.map((item) => {
          const active = tab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                active
                  ? 'bg-[#13322d] text-[#34d399] border border-[#214f48]'
                  : 'text-[#7d8799] bg-[#0c1018] border border-[#1f2635] hover:text-white'
              }`}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      {body}
    </div>
  );
}
